import React from "react";
import { Link } from "react-router-dom";

import { useEffect, useState } from "react";
import * as Yup from "yup";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import { capitalizeFirstLetter } from "../../utils/stringFunctions";
import { getInvitedMember, inviteMember, updateInvite } from "../../api/invite/memberServices";
import { UserRole, useAuth } from "../../context/AuthContext";
import { InviteStatus } from "../../models/UserInvite";
import { SKMultiSelect, SKMultiSelectOptions } from "../../components/SKMultiSelect";
import { SKSelectOption } from "../../components/SKSelectOption";
import APILoader from "../../components/APILoader";

interface AddMemberProps {
  invitationId?: string;
  onClose: () => void;
}

const AddMember = ({ invitationId, onClose }: AddMemberProps) => {
  const auth = useAuth();

  const [formData, updateFormData] = useState<{ name?: string; email?: string; role?: UserRole; status?: string; userId?: string }>({
    name: undefined,
    email: undefined,
    role: auth?.userRoles?.includes(UserRole.SkarvoAdmin) ? UserRole.SkarvoOperator : UserRole.Operator,
    status: undefined,
  });

  const [options, setOptions] = useState<SKMultiSelectOptions[] | undefined>(undefined);
  const [addingUser, setAddingUser] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);

  const schema = Yup.object().shape({
    name: Yup.string().required(`First name is required!`),
    email: Yup.string().email().required(`Email is required!`),
    role: Yup.string().required("role is required"),
  });

  useEffect(() => {
    // if (formData.status !== "invited") return;
    if (auth?.userRoles?.includes(UserRole.SkarvoAdmin) && !invitationId) {
      updateFormData(prev => ({ ...prev, role: auth?.userRoles?.includes(UserRole.SkarvoAdmin) ? UserRole.SkarvoOperator : UserRole.Operator }));
    }
  }, [auth, invitationId]);

  useEffect(() => {
    // if (formData.status !== "invited") return;
    if (auth?.userRoles?.includes(UserRole.SkarvoAdmin)) {
      setOptions([
        { title: "Skarvo admin", value: UserRole.SkarvoAdmin },
        { title: "Skarvo operator", value: UserRole.SkarvoOperator },
        { title: "Skarvo Channel Moderator", value: UserRole.SkarvoChannelModerator },
        { title: "Skarvo Content Operator", value: UserRole.SkarvoContentModerator },
      ]);
    }
    if (auth?.userRoles?.includes(UserRole.CreatorAdmin)) {
      setOptions([
        { title: "Creator admin", value: UserRole.CreatorAdmin },
        { title: "Creator operator", value: UserRole.Operator },
      ]);
    }
    if (auth?.userRoles?.includes(UserRole.BrandAdmin)) {
      setOptions([
        { title: "Brand admin", value: UserRole.BrandAdmin },
        { title: "Brand operator", value: UserRole.Operator },
      ]);
    }
  }, [auth, formData]);

  useEffect(() => {
    const getMember = async (invitationId: string) => {
      try {
        const member = await getInvitedMember(invitationId);
        const { name, email, role, status, userId } = member;
        updateFormData({ name, email, role, status, userId });
      } catch (error) {
        console.log(error);
      }
    };

    invitationId && getMember(invitationId);
  }, [invitationId]);

  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({ name: undefined, email: undefined, role: undefined });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({ name: undefined, email: undefined, role: undefined });
      // onClose();
    } catch (err) {
      const validationErrors: { [key: string]: string } = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach(err => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
      }
      setErrors(validationErrors);
    }
    if (!auth?.currentUser?.uid) return;
    setAddingUser(true);
    try {
      const invitedBy = auth?.currentUser?.displayName;
      const invitedByUserId = auth?.userRoles?.includes(UserRole.SkarvoAdmin) ? "user@skarvo" : auth?.currentUser?.uid;
      const channelId = auth?.myChannel?.id;
      const channelName = auth?.myChannel?.name;
      const { name, email, role, userId, status } = formData;
      if (invitationId) {
        if (status === "accepted") {
          await updateInvite(invitationId, { role });
        } else {
          await updateInvite(invitationId, { name, email: email?.toLowerCase() });
        }
      } else {
        if (invitedBy && email && name && role) {
          if (channelId) {
            await inviteMember({
              name,
              email: email.toLowerCase(),
              role,
              channelId,
              channelName,
              invitedBy,
              invitedByUserId,
              status: InviteStatus.pending,
            });
          } else {
            await inviteMember({ name, email: email.toLowerCase(), role, invitedBy, invitedByUserId, status: InviteStatus.pending });
          }
        }
      }
      onClose();
      //   navigate("./verify");
    } catch (err) {
      console.log(err);
    } finally {
      setAddingUser(false);
    }
  };

  //   useEffect(() => {
  //     const validate = async () => {
  //       try {
  //         setIsValidForm(true);
  //       } catch (err) {
  //         setIsValidForm(false);
  //       }
  //     };
  //     validate();
  //   }, [contentData, schema]);

  const isValid = () => {
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    setErrors({ firstName: "", email: "", lastName: "" });
    return () => {};
  }, []);

  // const [emailId, setEmailId] = useState(context?.formData?.email ?? "");

  const updateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newValue = { ...formData, [name]: value };
    updateFormData(newValue);
  };

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newValue = { ...formData, [name]: value };
    updateFormData(newValue);
  };

  return (
    <>
      {/* {addingUser && <Loader />} */}
      <APILoader loading={addingUser} />
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
        <div className="bg-transparent rounded shadow-lg ">
          <div className="flex flex-col gap-10">
            <div className="flex flex-col items-start formcard">
              <div className="flex flex-row items-start formheader">
                <div className="text-xl font-bold text-center mt-[15px] mb-[30px]">Send and Invite to a mebmer:</div>
              </div>

              <div className="mb-5 px-[30px] flex flex-col space-y-[10px] w-full">
                <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Full Name"
                  required
                  value={formData?.name ?? ""}
                  onChange={updateValue}
                />
                {errors.name && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.name}</span>}
                {!errors.name && <span className="block mb-2 text-sm text-gray-400 dark:text-gray-600">150 characters remaining</span>}
              </div>

              <div className="mb-5 px-[30px] flex flex-col space-x-[10x] w-full">
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="xyz@yourdomain.com"
                  required
                  value={formData?.email ?? ""}
                  onChange={updateValue}
                />
                {errors.email && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.email}</span>}
              </div>
              <div className="mb-5 px-[30px] flex flex-col space-x-[10x] w-full">
                {options && invitationId && formData.status === "accepted" ? (
                  <SKSelectOption
                    heading="Role"
                    name="role"
                    options={options}
                    value={formData.role}
                    onUpdate={e => {
                      let role = e.target.value as UserRole;
                      updateFormData(prev => ({ ...prev, role }));
                    }}
                  />
                ) : (
                  <>
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      Role
                    </label>
                    <span>Operator</span>
                  </>
                )}
                {/* <SKMultiSelect
                  onChange={values => {
                    setRoles(values);
                  }}
                  selectedValues={roles}
                  options={options}
                  editable={true}
                /> */}
                {errors.role && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.role}</span>}
              </div>
              <div className="thickseparator"></div>
              <div className="flex flex-col items-center justify-between w-full p-[30px] pt-0 md:flex-row-reverse  gap-y-[30px]">
                <Button to="#" onClick={handleSubmit} className="w-full md:w-auto md:min-w-[120px] formprimarybutton">
                  Continue
                </Button>
                <Link to="#" className="w-full md:w-auto md:min-w-[120px] formsecondarybutton" onClick={onClose}>
                  cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMember;
